import { ArrowLeft } from 'lucide-react';
import React from 'react';
import { Link } from 'react-router-dom';

interface AuthPageLayoutProps {
  children: React.ReactNode;
  showBackButton?: boolean;
  backTo?: string;
  backLabel?: string;
}

/**
 * Consistent layout for authentication pages (signin, signup, etc.)
 * - Fixed header with logo
 * - Back button (optional)
 * - Proper spacing for small screens
 * - Consistent styling across all auth pages
 */
export const AuthPageLayout: React.FC<AuthPageLayoutProps> = ({
  children,
  showBackButton = false,
  backTo = '/',
  backLabel = 'Landing page',
}) => {
  // CRITICAL: Ensure backTo is always a relative path, never a localhost URL
  const safeBackTo = React.useMemo(() => {
    if (!backTo) return '/';
    // Reject any localhost URLs
    if (backTo.includes('localhost') || backTo.includes('127.0.0.1') || backTo.includes('0.0.0.0')) {
      console.warn('[AuthPageLayout] Rejecting localhost backTo URL:', backTo);
      return '/';
    }
    // Ensure it's a relative path
    if (backTo.startsWith('http://') || backTo.startsWith('https://')) {
      console.warn('[AuthPageLayout] Converting absolute URL to relative path:', backTo);
      try {
        const url = new URL(backTo);
        return url.pathname + url.search;
      } catch {
        return '/';
      }
    }
    return backTo;
  }, [backTo]);

  return (
    <div className="min-h-screen bg-[#111010] font-['Inter']">
      {/* Fixed Header - Consistent across all auth pages */}
      <header className="fixed top-0 left-0 right-0 h-14 bg-[#221F1F] border-b border-[#504A4A] z-50 flex items-center px-4 relative">
        {/* Back Button - Positioned absolutely on left, uses safeBackTo */}
        {showBackButton && (
          <Link
            to={safeBackTo}
            className="absolute left-4 flex items-center gap-2 text-[#A9A3A3] hover:text-[#EBFFEE] transition-colors z-10"
            title={backLabel}
          >
            <ArrowLeft size={20} />
            <span className="text-sm font-medium hidden md:inline">{backLabel}</span>
          </Link>
        )}
        
        {/* Logo - Centered, always goes to home */}
        <Link
          to="/"
          className="flex items-center space-x-2 hover:opacity-80 transition-opacity mx-auto"
        >
          <img 
            src="/rollops-logo.png" 
            alt="RollOps Logo" 
            width={24} 
            height={24} 
            className="w-6 h-6" 
          />
          <span className="text-lg font-bold text-[#EBFFEE]">
            Roll<span className="text-rollops-accent">O</span>ps
          </span>
        </Link>
      </header>

      {/* Main Content - Offset by header height */}
      <main className="pt-14 min-h-screen flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>
    </div>
  );
};
